/**
 * A sample liability curve and register.
 *
 * Built so the screen's hardest case is the DEFAULT one: two thirds of the
 * register cannot be priced. That is what a real estate looks like early on —
 * the platform's own Madinah data prices two of six — and a mock that priced
 * everything would let a screen ship that leads with a total nobody should
 * lead with.
 *
 * The numbers are internally consistent: escalation is the deferral factor
 * applied over the years deferred, and the scenario totals are the sums of the
 * candidates they include. A mock whose arithmetic does not add up teaches a
 * reviewer to stop checking the arithmetic.
 */
import type { LiabilityCurve, LiabilityRegister } from "../domain/liability";

const CURRENCY = "SAR";

export function mockLiabilityCurve(fiscalYear: number): LiabilityCurve {
  return {
    available: true,
    fiscalYear,
    currency: CURRENCY,
    candidates: 6,
    priced: 4,
    notPriced: 2,
    notPricedDetail: [
      { title: "Recoat mandatory sign SGN-114", whyNot: "no unit rate for 'sign_recoat' at this site" },
      {
        title: "Pavement section predicted to fall below intervention PCI — PAV-014",
        whyNot: "no treatment category on the candidate",
      },
    ],
    coverageNote:
      "4 of 6 candidates carry a cost. The totals below exclude the other 2 entirely — they are not zero, they are unknown.",
    scenarios: [
      {
        envelope: 2_000_000,
        funded: 1,
        fundedCost: 1_940_000,
        deferred: 3,
        deferredCostNow: 3_060_000,
        costOfWaitingOneYear: 1_224_000,
        escalationNotComputed: 0,
        closureHoursPerYear: 186,
        closureKnownFor: 2,
        closureUnknownFor: 1,
        deferredDetail: [
          {
            title: "Seal TWY B, chainage 400–1300",
            escalation: 612_000,
            consequence: { known: true, closureHoursPerYear: 120 },
          },
          {
            title: "Resurface stand A12",
            escalation: 448_000,
            consequence: { known: true, closureHoursPerYear: 66 },
          },
          {
            title: "Joint reseal, apron north",
            escalation: 164_000,
            consequence: {
              known: false,
              whyNot: "the candidate names neither a zone nor an asset, so there is nothing to attribute closures to",
            },
          },
        ],
      },
      {
        envelope: 4_000_000,
        funded: 2,
        fundedCost: 3_820_000,
        deferred: 2,
        deferredCostNow: 1_180_000,
        costOfWaitingOneYear: 612_000,
        escalationNotComputed: 0,
        closureHoursPerYear: 120,
        closureKnownFor: 1,
        closureUnknownFor: 1,
        deferredDetail: [
          {
            title: "Seal TWY B, chainage 400–1300",
            escalation: 612_000,
            consequence: { known: true, closureHoursPerYear: 120 },
          },
          {
            title: "Joint reseal, apron north",
            escalation: 0,
            consequence: {
              known: false,
              whyNot: "the candidate names neither a zone nor an asset, so there is nothing to attribute closures to",
            },
          },
        ],
      },
      {
        envelope: 6_000_000,
        funded: 4,
        fundedCost: 5_000_000,
        deferred: 0,
        deferredCostNow: 0,
        costOfWaitingOneYear: 0,
        escalationNotComputed: 0,
        closureHoursPerYear: 0,
        closureKnownFor: 0,
        closureUnknownFor: 0,
        deferredDetail: [],
      },
    ],
  };
}

export function mockLiabilityRegister(): LiabilityRegister {
  return {
    candidates: 6,
    priced: 4,
    notPriced: 2,
    note: "Unpriced candidates are excluded from any total. They are unknown, not zero.",
    register: [
      {
        id: "L-1",
        title: "Seal TWY B, chainage 400–1300",
        category: "pavement",
        status: "candidate",
        forecastFor: "2027-06-01",
        priced: true,
        treatment: "crack_sealing",
        currency: CURRENCY,
        costNow: 1_020_000,
        costDeferred: 1_632_000,
        escalation: 612_000,
        deferYears: 1,
        basis: {
          unit: "m",
          rate: "48.00",
          pricedOn: "2026-01-01",
          rateSource: "2026 term contract",
          quantity: "21250.000",
          factorPerYear: "1.600",
          deferralBasis: "ASTM D5340 progression; sealing untreated becomes patching in two winters",
        },
      },
      {
        id: "L-2",
        title: "Resurface stand A12",
        category: "pavement",
        status: "candidate",
        forecastFor: "2027-09-01",
        priced: true,
        treatment: "overlay",
        currency: CURRENCY,
        costNow: 1_940_000,
        costDeferred: 2_388_000,
        escalation: 448_000,
        deferYears: 1,
        basis: {
          unit: "m²",
          rate: "310.00",
          pricedOn: "2026-03-01",
          rateSource: "2026 term contract",
          quantity: "6258.000",
          factorPerYear: "1.231",
          deferralBasis: "surface deterioration curve, tenant's own",
        },
      },
      {
        id: "L-3",
        title: "Joint reseal, apron north",
        category: "pavement",
        status: "candidate",
        forecastFor: "2027-04-01",
        priced: true,
        treatment: "joint_seal",
        currency: CURRENCY,
        costNow: 1_160_000,
        costDeferred: 1_324_000,
        escalation: 164_000,
        deferYears: 1,
        basis: {
          unit: "m",
          rate: "62.00",
          pricedOn: "2026-01-01",
          rateSource: "2026 term contract",
          quantity: "18710.000",
          factorPerYear: "1.141",
          deferralBasis: "tenant deferral curve for sealed joints",
        },
      },
      {
        id: "L-4",
        title: "Replace AGL series, TWY C",
        category: "lighting",
        status: "candidate",
        forecastFor: "2027-02-01",
        priced: true,
        treatment: "agl_replacement",
        currency: CURRENCY,
        costNow: 880_000,
        costDeferred: 880_000,
        escalation: 0,
        deferYears: 0,
        basis: {
          unit: "unit",
          rate: "4400.00",
          pricedOn: "2026-02-01",
          rateSource: "2026 term contract",
          quantity: "200.000",
          /* No curve for this category. Deferral is therefore treated as free,
             which is almost never true and makes the liability look smaller —
             so the register says so rather than hiding it in a zero. */
          factorPerYear: "1.000",
          deferralBasis: "no deferral curve on file for 'lighting' — deferral treated as free, which it is not",
        },
      },
      {
        id: "L-5",
        title: "Recoat mandatory sign SGN-114",
        category: "signage",
        status: "candidate",
        forecastFor: "2027-05-01",
        priced: false,
        whyNot: "no unit rate for 'sign_recoat' at this site",
      },
      {
        id: "L-6",
        title: "Pavement section predicted to fall below intervention PCI — PAV-014",
        category: "pavement",
        status: "candidate",
        forecastFor: "2027-08-01",
        priced: false,
        whyNot: "no treatment category on the candidate",
      },
    ],
  };
}
